import {
  AttachmentStrategy,
  attachmentStrategyToLabel,
  BETA_ATTACHMENT_STRATEGIES,
  BIGCOMMERCE_ONLY_ATTACHMENT_STRATEGIES,
  bigcommerceAttachmentStrategies,
  DEPRECATED_ATTACHMENT_STRATEGIES,
  NOT_SHOPIFY_COMPLIANT_ATTACHMENT_STRATEGIES,
} from "@redotech/redo-model/returns/attachment-strategy";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { FormSelectDropdown } from "@redotech/redo-web/select-dropdown";
import { Input } from "@redotech/ui/form";
import { memo } from "react";

interface AttachmentStrategyPickerProps {
  input: Input<AttachmentStrategy>;
  label?: string;
  description?: string;
  splitProducts: boolean;
  hasBigcommerceIntegration: boolean;
  placeholder?: string;
}

export const AttachmentStrategyPicker = memo(function AttachmentStrategyPicker({
  input,
  label = "Attachment strategy",
  description = "Placement in online store",
  splitProducts,
  hasBigcommerceIntegration,
  placeholder = "Default",
}: AttachmentStrategyPickerProps) {
  const dropdownOptions = attachmentStrategyDropdownOptions(
    splitProducts,
    hasBigcommerceIntegration,
  );

  return (
    <FormSelectDropdown
      description={description}
      input={input}
      label={label}
      options={[dropdownOptions]}
      placeholder={placeholder}
    >
      {(value) => <AttachmentStrategyLabel attachmentStrategy={value} />}
    </FormSelectDropdown>
  );
});

const AttachmentStrategyLabel = ({
  attachmentStrategy,
}: {
  attachmentStrategy: AttachmentStrategy;
}) => {
  const baseLabel = attachmentStrategyToLabel[attachmentStrategy];
  const labels = [];

  if (BETA_ATTACHMENT_STRATEGIES.includes(attachmentStrategy)) {
    labels.push(<RedoBadge color="blue" key="beta" size="xs" text="Beta" />);
  }
  if (DEPRECATED_ATTACHMENT_STRATEGIES.includes(attachmentStrategy)) {
    labels.push(
      <RedoBadge color="gray" key="deprecated" size="xs" text="Deprecated" />,
    );
  }
  if (
    NOT_SHOPIFY_COMPLIANT_ATTACHMENT_STRATEGIES.includes(attachmentStrategy)
  ) {
    labels.push(
      <RedoBadge
        color="orange"
        key="not-compliant"
        size="xs"
        text="Not Shopify compliant"
      />,
    );
  }
  if (BIGCOMMERCE_ONLY_ATTACHMENT_STRATEGIES.includes(attachmentStrategy)) {
    labels.push(
      <RedoBadge
        color="pink"
        key="bigcommerce-only"
        size="xs"
        text="Bigcommerce only"
      />,
    );
  }

  if (!labels.length) {
    return <>{baseLabel}</>;
  }

  return (
    <Flex align="center" gap="sm">
      <span>{baseLabel}</span>
      {labels}
    </Flex>
  );
};

export function attachmentStrategyDropdownOptions(
  splitProducts: boolean,
  isBigcommerce: boolean,
) {
  if (isBigcommerce) {
    return bigcommerceAttachmentStrategies;
  }

  const optionsWithoutSplittingProducts = [
    AttachmentStrategy.CHECKBOX,
    AttachmentStrategy.SINGLE_TOGGLE,
    AttachmentStrategy.CART_CARD,
    AttachmentStrategy.CHECK_OUT,
    AttachmentStrategy.SHIPPING,
    AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT,
    AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING,
    AttachmentStrategy.ANY_WIDGET_CUSTOM_FEE,
    AttachmentStrategy.ANY_WIDGET_PRODUCT,
  ];

  const optionsAvailableBasedOnSplitProductsSetting = splitProducts
    ? [
        ...optionsWithoutSplittingProducts,
        AttachmentStrategy.DOUBLE_TOGGLE,
        AttachmentStrategy.DOUBLE_CHECK_OUT,
      ]
    : optionsWithoutSplittingProducts;

  return optionsAvailableBasedOnSplitProductsSetting.filter(
    (strategy) => !BIGCOMMERCE_ONLY_ATTACHMENT_STRATEGIES.includes(strategy),
  );
}
